import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar, Star, Clock, ArrowRight, MapPin, ClipboardCheck, MessageSquare,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import { demandaDoCliente } from '@/lib/distribuicao';
import type { Evento, Demanda, EntregaEscopo, Anotacao, Contrato } from '@/lib/types';
import { FASES_ESCOPO } from '@/lib/types';

const FAM_COLOR: Record<string, string> = {
  'Conecta Folha Business': 'bg-blue-100 text-blue-800',
  'BRM - BUY': 'bg-purple-100 text-purple-800',
  'Setorial': 'bg-pink-100 text-pink-800',
  'Apex / Research': 'bg-green-100 text-green-800',
};

function daysFrom(d: Date): number {
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

function fmtDataCurta(iso?: string): { dia: string; mes: string } {
  if (!iso) return { dia: '—', mes: '' };
  const d = new Date(iso);
  return {
    dia: String(d.getDate()).padStart(2, '0'),
    mes: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
  };
}

export function HomePortal() {
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const [params] = useSearchParams();
  const asSuffix = params.get('as') ? `?as=${params.get('as')}` : '';

  const demandasQ = useQuery({ queryKey: ['portal', 'demandas'], queryFn: () => api.demandas.list().then(r => r.data) });
  const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
  const pendQ = useQuery({ queryKey: ['portal', 'pendencias'], queryFn: () => api.pendencias.list().then(r => r.data) });
  const escopoQ = useQuery({ queryKey: ['portal', 'escopo'], queryFn: () => api.escopoEntregas.list().then(r => r.data) });
  const contratosQ = useQuery({ queryKey: ['portal', 'contratos'], queryFn: () => api.contratos.list().then(r => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['portal', 'solicitantes'], queryFn: () => api.solicitantes.list().then(r => r.data) });

  const demandas: Demanda[] = useMemo(() => (demandasQ.data ?? []).filter(d => demandaDoCliente(d, sid)), [demandasQ.data, sid]);
  const escopo: EntregaEscopo[] = escopoQ.data ?? [];
  const eventos: Evento[] = eventosQ.data ?? [];
  const pendencias = pendQ.data ?? [];
  const meuContrato: Contrato | undefined = useMemo(() =>
    (contratosQ.data ?? []).find(c => c.solicitanteId === sid && c.status === 'Vigente'),
    [contratosQ.data, sid]);

  // Notas visíveis para o cliente (por contrato)
  const notasQ = useQuery({
    queryKey: ['portal', 'notas', meuContrato?.id],
    queryFn: () => meuContrato ? api.anotacoes.list('contrato', meuContrato.id).then(r => r.data) : Promise.resolve([]),
    enabled: !!meuContrato?.id,
  });
  const notasCliente: Anotacao[] = notasQ.data ?? [];

  const meusEventos = useMemo(() =>
    eventos.filter(e => (e.solicitantesIds ?? []).includes(sid))
           .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? '')),
    [eventos, sid]);

  const proxEvento = useMemo(() =>
    meusEventos.find(e => e.dataInicio && new Date(e.dataInicio) >= new Date()) ?? meusEventos[0],
    [meusEventos]);

  const aprovacao = demandas.filter(d => d.status === 'Aguardando aprovação');
  const escopoAprovacao = escopo.filter(e => e.status === 'Aguardando aprovação');
  const entregues = demandas.filter(d => d.status === 'Publicada').length;
  const escopoEntregues = escopo.filter(e => e.status === 'Entregue').length;
  const pendentesAbertas = pendencias.filter(p => p.status === 'Pendente');

  // oportunidades = 3 destaques (eventos fora do escopo)
  const oportunidades = useMemo(() =>
    eventos.filter(e => !(e.solicitantesIds ?? []).includes(sid))
           .filter(e => e.dataInicio && new Date(e.dataInicio) >= new Date())
           .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''))
           .slice(0, 3),
    [eventos, sid]);

  const proxDias = proxEvento?.dataInicio ? daysFrom(new Date(proxEvento.dataInicio)) : null;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text">Olá, {(solicitantesQ.data ?? []).find((s) => s.id === sid)?.nome ?? user?.nome?.split(' ')[0] ?? 'cliente'} 👋</h1>
        <p className="text-sm text-text-2">Resumo da sua parceria com a Apex Research</p>
      </div>

      {/* Hero pra aprovar */}
      {aprovacao.length > 0 && (
        <Link to={`/portal/aprovar${asSuffix}`}
          className="block bg-gradient-to-r from-red-50 via-red-100 to-red-200 border border-red-300 rounded-xl p-4
                     hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 text-white font-extrabold
                            flex items-center justify-center text-xl shadow-md shrink-0">
              {aprovacao.length}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-red-900">{aprovacao.length} {aprovacao.length === 1 ? 'peça pronta' : 'peças prontas'} pra sua revisão</h3>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {aprovacao.slice(0, 3).map(d => (
                  <span key={d.id} className="bg-white border border-red-200 text-red-900 text-[11px] font-medium px-2 py-0.5 rounded">
                    {d.titulo}
                  </span>
                ))}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-red-700 shrink-0" />
          </div>
        </Link>
      )}

      {/* Hero escopo pra aprovar */}
      {escopoAprovacao.length > 0 && (
        <Link to={`/portal/aprovar${asSuffix}`}
          className="block bg-gradient-to-r from-purple-50 via-purple-100 to-purple-200 border border-purple-300 rounded-xl p-4
                     hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 text-white font-extrabold
                            flex items-center justify-center text-xl shadow-md shrink-0">
              {escopoAprovacao.length}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-purple-900">{escopoAprovacao.length} {escopoAprovacao.length === 1 ? 'entrega do escopo pronta' : 'entregas do escopo prontas'} pra sua aprovação</h3>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {escopoAprovacao.slice(0, 3).map(e => (
                  <span key={e.id} className="bg-white border border-purple-200 text-purple-900 text-[11px] font-medium px-2 py-0.5 rounded">
                    {e.entrega}
                  </span>
                ))}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-purple-700 shrink-0" />
          </div>
        </Link>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="No meu plano" value={`${meusEventos.length} eventos`} sub={`${demandas.length} entregas previstas`} color="blue" />
        <Kpi label="Já entregues" value={`${entregues} / ${demandas.length}`} sub={demandas.length ? `${Math.round(100 * entregues / demandas.length)}% do contrato` : 'sem dados'} color="green" />
        <Kpi label="Oportunidades" value={String(eventos.length - meusEventos.length)} sub="eventos pra você participar" color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Próximo evento */}
        {proxEvento ? (
          <Link to={`/portal/eventos/${proxEvento.id}${asSuffix}`}
            className="bg-gradient-to-br from-[#001B3A] to-[#1565A8] text-white rounded-xl p-5 overflow-hidden
                       hover:shadow-xl transition-shadow relative">
            <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full bg-blue-400/30 blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold mb-2">📅 Próximo evento</div>
              <h2 className="text-2xl font-bold mb-1.5">{proxEvento.nome}</h2>
              <div className="text-xs opacity-85 space-y-0.5">
                {proxEvento.dataInicio && (
                  <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{new Date(proxEvento.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                )}
                {proxEvento.local && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{proxEvento.local}</div>}
              </div>
              {proxDias !== null && proxDias > 0 && (
                <div className="mt-4 inline-flex items-baseline gap-2 px-3 py-2 rounded-lg bg-white/15 border border-white/20">
                  <span className="text-3xl font-extrabold">{proxDias}</span>
                  <span className="text-xs opacity-80">dias pra começar</span>
                </div>
              )}
            </div>
          </Link>
        ) : (
          <div className="bg-panel border border-border rounded-xl p-5 text-center text-muted">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhum evento no seu plano</p>
          </div>
        )}

        {/* Pendências */}
        <div className="bg-panel border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-text uppercase tracking-wider">📤 Pendentes</h3>
            {pendentesAbertas.length > 0 && (
              <span className="bg-gradient-to-r from-orange-500 to-amber-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                {pendentesAbertas.length}
              </span>
            )}
          </div>
          {pendentesAbertas.length === 0 ? (
            <div className="text-xs text-muted py-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-1 opacity-40" />
              Nada pendente do seu lado por aqui
            </div>
          ) : (
            <div className="space-y-1.5">
              {pendentesAbertas.slice(0, 4).map(p => (
                <div key={p.id} className="flex items-center gap-2.5 py-2 border-b border-border/50 last:border-0">
                  <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center text-sm shrink-0">
                    {p.tipo === 'Lista de convidados' ? '👥' : p.tipo === 'Brief' ? '📝' : '📁'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-text truncate">{p.titulo}</div>
                    <div className="text-[10px] text-text-2 truncate">{p.descricao ?? ''}</div>
                  </div>
                  <Link to={`/portal/eventos/${p.eventoId ?? ''}${asSuffix}`} className="text-[10px] text-blue-light font-semibold">
                    Resolver →
                  </Link>
                </div>
              ))}
              <Link to={`/portal/entregas${asSuffix}`} className="text-[11px] text-blue-light hover:underline mt-2 inline-block">
                Ver todas →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Escopo do contrato */}
      {escopo.length > 0 && (
        <div className="bg-panel border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-text uppercase tracking-wider flex items-center gap-2">
              <ClipboardCheck className="w-3.5 h-3.5 text-blue-light" />
              Escopo do contrato
            </h3>
            <Link to={`/portal/entregas${asSuffix}`} className="text-[11px] text-blue-light font-semibold">
              Ver detalhes →
            </Link>
          </div>
          <div className="h-2.5 bg-panel-2 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-gradient-to-r from-green to-emerald-500 transition-all"
              style={{ width: `${escopo.length ? Math.round(100 * escopoEntregues / escopo.length) : 0}%` }} />
          </div>
          <div className="flex justify-between text-[11px] mb-3">
            <span><strong className="text-text">{escopoEntregues}</strong> entregues</span>
            <span className="text-text-2">{escopo.length ? Math.round(100 * escopoEntregues / escopo.length) : 0}% concluído</span>
            <span><strong className="text-text">{escopo.length - escopoEntregues}</strong> restantes</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {FASES_ESCOPO.map(fase => {
              const faseItems = escopo.filter(e => e.fase === fase);
              const faseEntregues = faseItems.filter(e => e.status === 'Entregue').length;
              if (faseItems.length === 0) return null;
              return (
                <div key={fase} className="text-center p-2 bg-panel-2/50 rounded-md">
                  <div className="text-lg font-extrabold text-text">{faseEntregues}/{faseItems.length}</div>
                  <div className="text-[9px] text-text-2 font-medium leading-tight mt-0.5">{fase}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comunicados da equipe */}
      {notasCliente.length > 0 && (
        <div className="bg-panel border border-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-text uppercase tracking-wider flex items-center gap-2 mb-3">
            <MessageSquare className="w-3.5 h-3.5 text-blue-light" />
            Comunicados da equipe
          </h3>
          <div className="space-y-2">
            {notasCliente.slice(0, 5).map(nota => (
              <div key={nota.id} className="border-l-2 border-blue-400 pl-3 py-1.5">
                <p className="text-sm text-text whitespace-pre-wrap">{nota.texto}</p>
                <span className="text-[10px] text-text-2 mt-1 block">
                  {nota.autorNome} · {new Date(nota.criadoEm).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Oportunidades em destaque */}
      {oportunidades.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-xs font-bold text-text uppercase tracking-wider flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-orange-500" />
              Oportunidades em destaque
            </h3>
            <Link to={`/portal/oportunidades${asSuffix}`} className="text-[11px] text-blue-light font-semibold">
              Ver todas →
            </Link>
          </div>
          <div className="space-y-2">
            {oportunidades.map(e => {
              const { dia, mes } = fmtDataCurta(e.dataInicio);
              return (
                <Link key={e.id} to={`/portal/oportunidades?evento=${e.id}${asSuffix ? `&as=${params.get('as')}` : ''}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50/40
                             border border-dashed border-orange-300 hover:border-orange-400 transition-colors">
                  <div className="w-12 p-1.5 rounded-md bg-white border border-orange-300 text-center shrink-0">
                    <div className="text-lg font-extrabold text-orange-600 leading-none">{dia}</div>
                    <div className="text-[8px] uppercase tracking-wider text-orange-500 font-bold">{mes}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[12.5px] text-orange-900 truncate">
                      {e.nome}
                      {e.familia && <span className={clsx('ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase', FAM_COLOR[e.familia] ?? 'bg-gray-100 text-gray-700')}>{e.familia.replace('Conecta Folha Business', 'Conecta')}</span>}
                    </div>
                    <div className="text-[10px] text-orange-700">📍 {e.local ?? '—'}</div>
                  </div>
                  <button className="bg-gradient-to-r from-orange-500 to-amber-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-md whitespace-nowrap">
                    ✨ Quero participar
                  </button>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub: string; color: 'blue' | 'green' | 'orange' }) {
  const cls = {
    blue: 'border-blue/40',
    green: 'border-green/40',
    orange: 'border-orange/40',
  }[color];
  return (
    <div className={clsx('bg-panel border border-border border-2 rounded-lg p-3', cls)}>
      <div className="text-[10px] uppercase tracking-wider text-muted font-bold">{label}</div>
      <div className="text-xl font-extrabold text-text mt-1">{value}</div>
      <div className="text-[10.5px] text-text-2 mt-0.5">{sub}</div>
    </div>
  );
}

// Mail import unused but ok

